import React from 'react';
import { Clock } from 'lucide-react';
import {
  type SchedulePickerState,
  type SchedulePickerKind,
  compilePickerToCron,
  humanReadableCron,
  nextNRuns,
  isValidCron,
} from '../../utils/cron';

interface Props {
  value: SchedulePickerState;
  onChange: (state: SchedulePickerState) => void;
  timezone: string;
}

const FREQUENCY_OPTIONS: Array<{ value: SchedulePickerKind; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'custom', label: 'Custom (cron)' },
];

const DAYS_OF_WEEK = [
  { value: 0, short: 'S', long: 'Sun' },
  { value: 1, short: 'M', long: 'Mon' },
  { value: 2, short: 'T', long: 'Tue' },
  { value: 3, short: 'W', long: 'Wed' },
  { value: 4, short: 'T', long: 'Thu' },
  { value: 5, short: 'F', long: 'Fri' },
  { value: 6, short: 'S', long: 'Sat' },
];

const inputBase =
  'px-3 py-2 rounded-xl text-sm bg-white dark:bg-white/5 border border-input dark:border-white/10 text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary/40 transition-colors';

const TimeOfDayInput: React.FC<{
  hour: number;
  minute: number;
  onChange: (hour: number, minute: number) => void;
}> = ({ hour, minute, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-foreground mb-1">
      Time of day
    </label>
    <div className="flex items-center gap-2">
      <select
        value={hour}
        onChange={(e) => onChange(Number(e.target.value), minute)}
        className={inputBase}
      >
        {Array.from({ length: 24 }, (_, h) => (
          <option key={h} value={h}>
            {String(h).padStart(2, '0')}
          </option>
        ))}
      </select>
      <span className="text-muted-foreground">:</span>
      <select
        value={minute}
        onChange={(e) => onChange(hour, Number(e.target.value))}
        className={inputBase}
      >
        {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
          <option key={m} value={m}>
            {String(m).padStart(2, '0')}
          </option>
        ))}
      </select>
    </div>
  </div>
);

const DayOfWeekChips: React.FC<{
  value: number[];
  onChange: (days: number[]) => void;
}> = ({ value, onChange }) => {
  const toggle = (day: number) => {
    onChange(value.includes(day) ? value.filter((d) => d !== day) : [...value, day]);
  };
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1">
        Day(s) of week
      </label>
      <div className="flex gap-1.5">
        {DAYS_OF_WEEK.map((d) => {
          const active = value.includes(d.value);
          return (
            <button
              key={d.value}
              type="button"
              onClick={() => toggle(d.value)}
              title={d.long}
              className={`w-9 h-9 rounded-lg text-sm font-medium border transition-colors ${
                active
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white dark:bg-white/5 text-foreground border-input dark:border-white/10 hover:bg-accent'
              }`}
            >
              {d.short}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DayOfMonthInput: React.FC<{
  value: number;
  onChange: (day: number) => void;
}> = ({ value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-foreground mb-1">
      Day of month
    </label>
    <input
      type="number"
      min={1}
      max={31}
      value={value}
      onChange={(e) => onChange(Math.max(1, Math.min(31, Number(e.target.value) || 1)))}
      className={`${inputBase} w-24`}
    />
    <p className="text-xs text-muted-foreground mt-1">
      Months without this day (e.g. day 31 in February) are skipped.
    </p>
  </div>
);

export const ScheduleField: React.FC<Props> = ({ value, onChange, timezone }) => {
  const cron = compilePickerToCron(value);
  const valid = isValidCron(cron);
  const next = valid ? nextNRuns(cron, timezone) : [];
  const human = valid ? humanReadableCron(cron) : null;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Frequency
        </label>
        <select
          value={value.kind}
          onChange={(e) => onChange({ ...value, kind: e.target.value as SchedulePickerKind })}
          className={`${inputBase} w-full md:w-64`}
        >
          {FREQUENCY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {value.kind === 'hourly' && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Minute of the hour
          </label>
          <select
            value={value.minute}
            onChange={(e) => onChange({ ...value, minute: Number(e.target.value) })}
            className={`${inputBase} w-32`}
          >
            {Array.from({ length: 60 }, (_, m) => (
              <option key={m} value={m}>
                :{String(m).padStart(2, '0')}
              </option>
            ))}
          </select>
        </div>
      )}

      {(value.kind === 'daily' || value.kind === 'weekly' || value.kind === 'monthly') && (
        <TimeOfDayInput
          hour={value.hour}
          minute={value.minute}
          onChange={(hour, minute) => onChange({ ...value, hour, minute })}
        />
      )}

      {value.kind === 'weekly' && (
        <DayOfWeekChips
          value={value.daysOfWeek}
          onChange={(daysOfWeek) => onChange({ ...value, daysOfWeek })}
        />
      )}

      {value.kind === 'monthly' && (
        <DayOfMonthInput
          value={value.dayOfMonth}
          onChange={(dayOfMonth) => onChange({ ...value, dayOfMonth })}
        />
      )}

      {value.kind === 'custom' && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Cron expression (5 fields)
          </label>
          <input
            type="text"
            value={value.customCron}
            onChange={(e) => onChange({ ...value, customCron: e.target.value })}
            placeholder="0 9 * * 1-5"
            spellCheck={false}
            className={`${inputBase} w-full font-mono`}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Standard Unix cron — minute, hour, day-of-month, month, day-of-week.
          </p>
        </div>
      )}

      <div className="rounded-lg bg-muted dark:bg-white/[0.03] border border-border p-3">
        <div className="flex items-start gap-2">
          <Clock className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm flex-1">
            {valid ? (
              <>
                <div className="font-medium text-foreground">{human}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  <span className="font-mono">{cron}</span> · {timezone}
                </div>
                {next.length > 0 ? (
                  <ul className="text-xs text-muted-foreground mt-2 space-y-0.5">
                    <li className="font-medium text-foreground">Next 3 runs:</li>
                    {next.map((d, i) => (
                      <li key={i}>
                        ·{' '}
                        {d.toLocaleString('en-US', {
                          timeZone: timezone,
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            ) : (
              <div className="text-destructive">
                Invalid cron expression — please fix before saving.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleField;
